import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useThemeColors } from '@/shared/theme/useThemeColors';
import { semantic, typography, shadow, radius } from '@/shared/theme/tokens';

/**
 * Tag-style recipient input: pick from a suggestion list (deployment users) or
 * type a value and press Enter/comma to add. Used for email To/CC and WhatsApp
 * phone numbers.
 *
 * The suggestion menu renders in a portal (document.body) positioned under the
 * field, so it is never clipped by the form's overflow/scroll containers.
 *
 * @param {object}   props
 * @param {string[]} props.value         current recipients
 * @param {(next:string[])=>void} props.onChange
 * @param {{value:string,label:string}[]} [props.suggestions]  pickable options (e.g. users)
 * @param {(v:string)=>boolean} props.validate   true if a typed value is acceptable
 * @param {string}   [props.placeholder]
 * @param {boolean}  [props.disabled]
 */
export function RecipientInput({ value = [], onChange, suggestions = [], validate, placeholder, disabled }) {
  const c = useThemeColors();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  const filtered = useMemo(() => {
    const q = text.trim().toLowerCase();
    return suggestions
      .filter((s) => s.disabled || !value.includes(s.value)) // keep already-added out, but keep disabled (phoneless) visible
      .filter((s) => !q || s.label.toLowerCase().includes(q) || String(s.value ?? '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [suggestions, value, text]);

  const showMenu = open && filtered.length > 0;

  // Track the field's viewport rect so the portal menu sits right under it.
  const measure = useCallback(() => {
    if (wrapRef.current) setRect(wrapRef.current.getBoundingClientRect());
  }, []);

  useLayoutEffect(() => {
    if (showMenu) measure();
  }, [showMenu, value.length, text, measure]);

  // Reposition on scroll/resize while the menu is open (it lives in a portal,
  // so it doesn't move with the scrolling form on its own).
  useEffect(() => {
    if (!showMenu) return undefined;
    const onMove = () => measure();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [showMenu, measure]);

  function add(raw) {
    const v = String(raw).trim();
    if (!v || value.includes(v)) { setText(''); return; }
    if (validate && !validate(v)) return; // keep text so the user can fix it
    onChange([...value, v]);
    setText('');
    setOpen(false);
  }

  function remove(v) { onChange(value.filter((x) => x !== v)); }

  function onKeyDown(e) {
    if ((e.key === 'Enter' || e.key === ',') && text.trim()) {
      e.preventDefault();
      add(text);
    } else if (e.key === 'Backspace' && !text && value.length) {
      remove(value[value.length - 1]);
    }
  }

  const invalidText = text.trim() && validate && !validate(text.trim());

  return (
    <div style={{ position: 'relative' }} ref={wrapRef}>
      <div
        onClick={() => !disabled && inputRef.current?.focus()}
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
          minHeight: 44, padding: '7px 10px', borderRadius: radius.lg,
          background: c.bgSubtle, border: `1.5px solid ${invalidText ? semantic.error : c.borderInput}`,
          cursor: disabled ? 'default' : 'text',
        }}
      >
        {value.map((v) => (
          <span key={v} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: radius.sm,
            background: c.bg, border: `1px solid ${c.borderSubtle}`, fontSize: typography.fontSize.base, color: c.textPrimary,
          }}>
            {v}
            {!disabled && (
              <button onClick={(e) => { e.stopPropagation(); remove(v); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: c.textSubtle }} title="Remove">
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>close</span>
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <input
            ref={inputRef}
            value={text}
            onChange={(e) => { setText(e.target.value); setOpen(true); }}
            onKeyDown={onKeyDown}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 120)}
            placeholder={value.length ? '' : placeholder}
            style={{
              flex: 1, minWidth: 120, border: 'none', outline: 'none', background: 'transparent',
              fontSize: typography.fontSize.md, color: c.textPrimary, fontFamily: typography.fontFamily.body,
            }}
          />
        )}
      </div>

      {invalidText && (
        <p style={{ margin: '4px 0 0', fontSize: typography.fontSize.sm, color: semantic.error }}>Invalid format — press Enter to retry once fixed.</p>
      )}

      {showMenu && rect && createPortal(
        <div style={{
          position: 'fixed', top: rect.bottom + 4, left: rect.left, width: rect.width, zIndex: 4000,
          background: c.bg, border: `1px solid ${c.borderSubtle}`, borderRadius: radius.md, overflow: 'hidden',
          boxShadow: shadow.card, maxHeight: 220, overflowY: 'auto',
        }}>
          {filtered.map((s) => (
            <button
              key={s.key ?? s.value ?? s.label}
              disabled={s.disabled}
              onMouseDown={(e) => { e.preventDefault(); if (!s.disabled) add(s.value); }}
              style={{
                display: 'flex', flexDirection: 'column', gap: 1, width: '100%', textAlign: 'left',
                padding: '8px 12px', border: 'none', background: 'transparent',
                cursor: s.disabled ? 'not-allowed' : 'pointer', opacity: s.disabled ? 0.55 : 1,
              }}
              onMouseEnter={(e) => { if (!s.disabled) e.currentTarget.style.background = c.bgSubtle; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ fontSize: typography.fontSize.md, color: c.textPrimary }}>{s.label}</span>
              {s.note
                ? <span style={{ fontSize: typography.fontSize.sm, color: semantic.warning }}>{s.note}</span>
                : (s.value && s.label !== s.value && <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle }}>{s.value}</span>)}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
